<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import type { GroupOfAdminsCreateSchema } from '@modules/groups-of-admins/submodules/create/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { GROUPS_OF_ADMINS_LIST_ROUTE_NAME } from '@modules/groups-of-admins/constants';
import { TRANSLATION_GROUPS_OF_ADMINS_CREATE } from '@modules/groups-of-admins/submodules/create/constants';
import { useGroupsOfAdminsCreateMutation } from '@modules/groups-of-admins/submodules/create/mutations';
import { useGroupOwnersListQuery } from '@modules/groups-of-admins/submodules/create/queries';
import { useI18n } from '@modules/i18n/composables';

const { getTranslation } = useI18n();
const router = useRouter();

const {
  data: dataOptions,
  isLoading: isQueryLoading,
} = useGroupOwnersListQuery();

const {
  mutateAsync: createAdminGroup,
  isLoading: isMutateLoading,
  clearError,
} = useGroupsOfAdminsCreateMutation();

const ownersDataOptions = computed<IFormSelectInputOption<number>[]>(() => {
  if (!dataOptions.value) {
    return [];
  }

  return dataOptions.value.map((item) => ({
    value: item.id,
    label: `${item.name}`,
  }));
});

async function handleCreate(data: GroupOfAdminsCreateSchema): Promise<void> {
  await createAdminGroup(data);
  router.back({ name: GROUPS_OF_ADMINS_LIST_ROUTE_NAME });
}

const formSchema = computed<FormSchema<GroupOfAdminsCreateSchema>>(() => ({
  admin_id: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUPS_OF_ADMINS_CREATE, 'CHOOSE_OWNER').value,
    required: true,
    options: ownersDataOptions.value,
  },
}));
</script>

<template>
  <VCard v-data-qa-auto-id="'GroupsOfAdminsCreatePage'"
    :title="$getTranslation(TRANSLATION_GROUPS_OF_ADMINS_CREATE, 'HEADER_SETTINGS')"
    type="form"
  >
    <VLoader
      :loading="isQueryLoading"
      min-height="400px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUPS_OF_ADMINS_CREATE, 'CREATE_BTN')"
        :is-loading="isMutateLoading"
        @submit="handleCreate($event)"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUPS_OF_ADMINS_CREATE, 'BACK')"
            :to="{ name: GROUPS_OF_ADMINS_LIST_ROUTE_NAME }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
